import {HttpStatusCode} from '@angular/common/http';
import {ChangeDetectorRef, Component, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {
    BucketName,
    IncidentApiService,
    IncidentResponse,
    IncidentSubtype,
    IncidentType,
    Participant,
    ReferenceBookResponse,
    ResolutionApiService,
    ResolutionStatus,
    ResolutionSubject,
    SubjectCategory,
} from '@npck/af-frontend-api';
import {formatToServer, LocalizationService, NESTED_TAB, RouterService, rxSpinner, TAB} from '@npck/libs/commons';
import {CalendarComponent, SnackbarService} from '@npck/libs/ui-kit';
import dayjs from 'dayjs';
import {zip} from 'rxjs';
import {DateForm} from '../../../../components/date-picker-field/date-picker-field.component';
import {AttachmentsForm, createAttachmentsRequest} from '../../../../components/files/files.component';
import {PhoneNumberComponent} from '../../../../components/phone-number/phone-number.component';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {
    DESCRIPTION_MAX_LENGTH,
    INNOCENT_RESOLUTION_STATUSES,
    MAX_FILES_COUNT,
    UNIFIED_REGISTER_MAX_LENGTH,
    UNIFIED_REGISTER_PATTERN,
} from '../../../../domain/constants';
import {DictionaryHolderService, findValueInDictionary} from '../../../../domain/dictionary-holder.service';
import {getValidPhoneNumberOrNull} from '../../../../domain/phone-utils';
import {INCIDENT_ID} from '../../../../domain/query-params.keys';
import {DATE_FORMATS} from '../../../../infra/date-time.adapter';
import {Path} from '../../../../path';
import {IncidentsTab} from '../../incidents.component';
import {IncidentDetailsTab} from '../incident-details.component';

const SUBTYPE_FILTER_KEY: Record<string, string> = {
    [IncidentSubtype.DRUG]: 'drug',
    [IncidentSubtype.CASINO]: 'casino',
    [IncidentSubtype.PYRAMID]: 'pyramid',
    [IncidentSubtype.DRUG_CEPI]: 'drug_cepi',
    [IncidentSubtype.CASINO_CEPI]: 'casino_cepi',
    [IncidentSubtype.PYRAMID_CEPI]: 'pyramid_cepi',
};

@Component({
    templateUrl: './resolution-create.component.html',
    standalone: false,
})
export class ResolutionCreateComponent {
    @ViewChild('datepicker') picker: CalendarComponent;
    @ViewChild('phoneNumberComponent') phoneNumberComponent: PhoneNumberComponent;
    Validators = Validators;
    DATE_FORMATS = DATE_FORMATS;
    DESCRIPTION_MAX_LENGTH = DESCRIPTION_MAX_LENGTH;
    UNIFIED_REGISTER_MAX_LENGTH = UNIFIED_REGISTER_MAX_LENGTH;
    incidentId: string;
    form: FormGroup<ResolutionForm>;
    categoryDictionary: ReferenceBookResponse[];
    resolutionStatusDictionary: ReferenceBookResponse[];
    categories: ReferenceBookResponse[];
    allowedCategoryDictionary: ReferenceBookResponse[] = [];
    subjects: SubjectRow[] = [];
    categoryControls: FormControl<string | null>[] = [];
    MAX_FILES_COUNT = MAX_FILES_COUNT;
    BucketName = BucketName;
    resolutionStatus: string = '';
    private wasInnocentStatus: boolean = false;
    private backToTab: IncidentsTab;
    private backToNestedTab: IncidentDetailsTab;

    constructor(
        private authHolderService: AuthHolderService,
        private activatedRoute: ActivatedRoute,
        private routerService: RouterService,
        private formBuilder: FormBuilder,
        private incidentApiService: IncidentApiService,
        private resolutionApiService: ResolutionApiService,
        private snackbarService: SnackbarService,
        private cdr: ChangeDetectorRef,
        private localizationService: LocalizationService,
        dictionaryHolderService: DictionaryHolderService,
    ) {
        if (!this.authHolderService.hasAccess(['government-user'])) {
            this.navigateBack();
        }

        this.incidentId = this.activatedRoute.snapshot.paramMap.get(INCIDENT_ID);
        this.backToTab = this.activatedRoute.snapshot.queryParamMap.get(TAB) as IncidentsTab;
        this.backToNestedTab = this.activatedRoute.snapshot.queryParamMap.get(NESTED_TAB) as IncidentDetailsTab;

        zip([
            dictionaryHolderService.getReference('incident_status'),
            this.incidentApiService.getIncident(this.incidentId),
            dictionaryHolderService.getReference('category'),
        ])
            .subscribe({
                next: ([statuses, response, categories]) => {
                    const isOtherOrCepi = response.typeCode === IncidentType.OTHER
                        || response.typeCode === IncidentType.COMPROMISED_PAYMENT_INSTRUMENT;
                    this.resolutionStatus = response.resolutionStatusCode;
                    this.subjects = this.extractParticipant(response);
                    this.categoryDictionary = this.updateCategories(response.fraudTypeCode, categories);
                    this.categories = categories;
                    this.allowedCategoryDictionary = this.buildAllowedCategoryDictionary(isOtherOrCepi, response.fraudTypeCode, categories);
                    this.categoryControls = this.subjects.map(s => {
                        const control = new FormControl<SubjectCategory | null>(s.category ?? null, Validators.required);
                        if (s.category === SubjectCategory.WHITE_LIST) {
                            control.disable();
                        } else {
                            control.valueChanges.subscribe(val => s.category = val ?? null);
                        }
                        return control;
                    });

                    let allowed: Set<string>;
                    if (isOtherOrCepi) {
                        allowed = new Set(['NOT_CONFIRMED', 'CONFIRMED', 'P']);
                    } else {
                        if (response.incidentStatusCode === 'ADD') {
                            allowed = new Set(['F', 'NF', 'P']);
                        } else {
                            allowed = new Set(['P', 'NF']);
                        }
                    }
                    this.resolutionStatusDictionary = statuses.filter(s =>
                        s.code1 === 'resolution' &&
                        s.code !== 'R' &&
                        allowed.has(s.code),
                    );

                    this.buildForm();
                },
                error: err => {
                    if (err.status !== HttpStatusCode.NotFound) {
                        throw err;
                    } else {
                        this.buildForm();
                    }
                },
            });
    }

    get isRestrictedCategoryResolution(): boolean {
        const status = this.form?.controls.resolutionStatus.value;
        return status === ResolutionStatus.F || status === ResolutionStatus.CONFIRMED;
    }

    get hasInvalidCategory(): boolean {
        return this.isRestrictedCategoryResolution && this.categoryControls.some(control => control.invalid);
    }

    filterIncidentIdPath = (path: string): boolean => {
        return path === this.incidentId;
    };

    onUnifiedRegisterNumberInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.form.controls.unifiedRegisterNumber.setValue(input.value.replace(/\D/g, '').slice(0, UNIFIED_REGISTER_MAX_LENGTH));
    }

    createResolution(): void {
        this.form.markAllAsTouched();
        if (this.isRestrictedCategoryResolution) {
            this.categoryControls.forEach(control => control.markAsTouched());
        }
        if (this.form.invalid || this.hasInvalidCategory) {
            return;
        }
        const raw = this.form.getRawValue();
        this.resolutionApiService.createResolution(this.incidentId, {
            status: raw.resolutionStatus as ResolutionStatus,
            bin: raw.govOrganization,
            investigatorFullName: raw.investigatorFullName,
            investigatorPhoneNumber: getValidPhoneNumberOrNull(raw.phoneNumber, this.phoneNumberComponent.selectedCountryCode),
            unifiedRegisterNumber: raw.unifiedRegisterNumber,
            unifiedRegisterDate: formatToServer(dayjs(raw.unifiedRegisterDate.date)),
            comment: raw.resolutionDescription,
            attachments: createAttachmentsRequest(this.form.controls.files.value),
            subjects: this.buildSubjectsPayload(),
        })
            .pipe(rxSpinner('createResolution'))
            .subscribe(() => {
                this.snackbarService.showSnackBar('i18n_af_snackBarMessageIncidentResolutionCreated');
                this.navigateBack();
            });
    }

    navigateBack(): void {
        this.routerService.navigate(`${Path.INCIDENTS}/${this.incidentId}`, {
            queryParams: {
                [TAB]: this.backToTab,
                [NESTED_TAB]: this.backToNestedTab,
            },
        });
    }

    getParticipantDisplayValue(s: any): string {
        if (!s) {
            return '';
        }
        const parts: string[] = [];
        if (s.surname) {
            parts.push(s.surname);
        }
        if (s.name) {
            parts.push(s.name);
        }
        if (s.patronymic) {
            parts.push(s.patronymic);
        }
        const fieldName = s.subjectType === 'ORGANIZATION' ?
            this.localizationService.getLocalizedKey(`i18n_af_labelBin`) :
            this.localizationService.getLocalizedKey(`i18n_af_labelIin`)
        ;
        const fio = parts.join(' ').trim();
        const identificationNumberValue = s.identificationNumber ? `, ` + fieldName + `: ${s.identificationNumber}` : '';
        return fio + identificationNumberValue;
    }

    getCategories(category: SubjectCategory): ReferenceBookResponse[] {
        const exists = this.categoryDictionary.some(c => c.code === category);
        if (exists) {
            return this.categoryDictionary;
        }
        const found = this.categories.find(c => c.code === category);
        if (found) {
            return [...this.categoryDictionary, found];
        }
        return this.categoryDictionary;
    }

    getCategoryName(code: string | null): string {
        return findValueInDictionary(this.categories ?? [], code ?? '', 'name_ru');
    }

    private buildForm(): void {
        this.form = this.formBuilder.group({
            resolutionStatus: new FormControl(null, [Validators.required]),
            govOrganization: new FormControl({value: this.authHolderService.organizationBin, disabled: true}, [Validators.required]),
            investigatorFullName: new FormControl(this.authHolderService.fullName, [Validators.required]),
            phoneNumber: new FormControl(this.authHolderService.phoneNumber, [Validators.required]),
            unifiedRegisterNumber: new FormControl(null, [Validators.required, Validators.pattern(UNIFIED_REGISTER_PATTERN)]),
            unifiedRegisterDate: this.formBuilder.group({
                date: new FormControl(null, [Validators.required]),
            }),
            resolutionDescription: new FormControl(null, [Validators.required, Validators.maxLength(DESCRIPTION_MAX_LENGTH)]),
            files: this.formBuilder.group({}),
        });
        this.form.controls.resolutionStatus.valueChanges.subscribe(status => {
            this.handleResolutionStatusChange(status);
        });
        this.cdr.detectChanges();
    }

    private handleResolutionStatusChange(status: string): void {
        const isInnocentStatus = INNOCENT_RESOLUTION_STATUSES.includes(status as ResolutionStatus);
        const changed = isInnocentStatus !== this.wasInnocentStatus;
        this.wasInnocentStatus = isInnocentStatus;
        if (changed) {
            this.categoryControls.forEach((control, index) => {
                const subject = this.subjects[index];
                if (subject.category === SubjectCategory.WHITE_LIST) { return; }

                if (isInnocentStatus) {
                    subject.savedCategory = subject.category;
                    subject.category = SubjectCategory.WITNESS;
                    control.setValue(SubjectCategory.WITNESS);
                    control.disable();
                } else {
                    const restored = subject.savedCategory ?? subject.initialCategory;
                    subject.category = restored;
                    control.setValue(restored);
                    control.enable();
                }
            });
        }
        this.clearChangedDisallowedCategories();
    }

    private clearChangedDisallowedCategories(): void {
        if (!this.isRestrictedCategoryResolution) {
            return;
        }
        this.categoryControls.forEach((control, index) => {
            const subject = this.subjects[index];
            const value = control.value as SubjectCategory | null;
            if (value !== null
                && value !== subject.initialCategory
                && !this.allowedCategoryDictionary.some(c => c.code === value)) {
                control.setValue(null);
                control.markAsTouched();
            }
        });
    }

    private updateCategories(fraudTypeCode: string, categories: ReferenceBookResponse[]): ReferenceBookResponse[] {
        const filterKey = SUBTYPE_FILTER_KEY[fraudTypeCode] ?? 'fraud';
        return categories.filter(c => c.code !== SubjectCategory.WHITE_LIST && (c.code1 === filterKey || c.code1 === 'all'));
    }

    private buildAllowedCategoryDictionary(
        isOtherOrCepi: boolean,
        fraudTypeCode: string,
        categories: ReferenceBookResponse[],
    ): ReferenceBookResponse[] {
        if (!isOtherOrCepi) {
            return categories.filter(c =>
                c.code === SubjectCategory.BLACK ||
                c.code === SubjectCategory.DROPPER ||
                c.code === SubjectCategory.WITNESS);
        }
        const filterKey = SUBTYPE_FILTER_KEY[fraudTypeCode] ?? null;
        return categories.filter(c =>
            c.code === SubjectCategory.DROPPER ||
            c.code === SubjectCategory.WITNESS ||
            (filterKey !== null && c.code1 === filterKey));
    }

    private buildSubjectsPayload(): ResolutionSubject[] {
        return (this.subjects ?? [])
            .filter(s => !!s.identificationNumber && !!s.category)
            .map((s): ResolutionSubject => ({
                subjectId: s.subjectId!,
                incidentId: s.incidentId!,
                name: s.name,
                surname: s.surname,
                patronymic: s.patronymic,
                category: s.category!,
                prevCategory: s.initialCategory,
                isChanged: s.initialCategory !== s.category,
            }));
    }

    private extractParticipant(incident: IncidentResponse): SubjectRow[] {
        const rows: SubjectRow[] = (incident?.transactions ?? [])
            .flatMap(tx => [tx.sender, tx.receiver])
            .filter((p): p is Participant =>
                !!p?.incident_role?.trim() && p.incident_role !== 'VICTIM' && p.identification_number != null)
            .map(p => ({
                subjectType: p.type,
                surname: p.info?.surname ?? null,
                name: p.info?.name ?? null,
                patronymic: p.info?.patronymic ?? null,
                identificationNumber: p.identification_number ?? null,
                category: p.category,
                initialCategory: p.category,
                participantId: p.id,
                incidentId: incident.id,
                subjectId: p.subject_id ?? null,
            }));

        const seen = new Set<string>();
        return rows.filter(r => {
            if (!r.identificationNumber) { return true; }
            const key = `${r.subjectType}:${r.identificationNumber}`;
            if (seen.has(key)) { return false; }
            seen.add(key);
            return true;
        });
    }
}

interface ResolutionForm {
    resolutionStatus: FormControl<string>;
    govOrganization: FormControl<string>;
    investigatorFullName: FormControl<string>;
    phoneNumber: FormControl<string>;
    unifiedRegisterNumber: FormControl<string>;
    unifiedRegisterDate: FormGroup<DateForm>;
    resolutionDescription: FormControl<string>;
    files: FormGroup<AttachmentsForm>;
}

type SubjectRow = {
    subjectType: string | null;
    incidentId: string | null;
    surname: string | null;
    name: string | null;
    patronymic: string | null;
    identificationNumber: string | null;
    category: SubjectCategory | null;
    initialCategory?: SubjectCategory | null;
    savedCategory?: SubjectCategory | null;
    subjectId: string | null;
};
